import { Box, Card } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import Quill from 'quill';
import { getPossibleTicketUsers, postAddTicket } from '../../../../api/tickets';
import { addLinkedTicketsToOrder } from '../../../../redux/slices/orders';
import {
  getFacilitiesAndUsersList,
  getTicketData,
  getTicketMessages,
  sendTicketMessage,
} from '../../../../redux/slices/tickets';
import { useDispatch, useSelector } from '../../../../redux/store';
import Editor from '../../../../reusable-components/editor';
import { useSettingsCheck } from '../../../../reusable-components/settings';
import TicketCategory from '../../tickets/ticket/TicketCategory';
import TicketMessageList from '../../tickets/ticket/TicketMessageList';
import TicketButtons from './TicketButtons';
import TicketHeader from './TicketHeader';
import { fCurrency } from '../../../../utils/formatNumber';
import { useTabs } from '../../../../context/TabContext';
import { ticketMessageInput } from '../../tickets/ticket/newTicket/ticket-message-input-cleanup';
import { handleAddTicketAttachment } from '../../tickets/ticket/newTicket/TicketHelper';

OrderTicket.propTypes = {
  setOpenTicket: PropTypes.func,
  openTicket: PropTypes.string,
  setAddItemsToTicket: PropTypes.func,
  addItemsToTicket: PropTypes.bool,
  selectedRows: PropTypes.array,
  setSelected: PropTypes.func,
  setCurrentTicket: PropTypes.func,
  currentTicket: PropTypes.number,
  openSidebar: PropTypes.bool,
  setOpenSidebar: PropTypes.func,
  isOrderInfoContainerOpen: PropTypes.bool,
  setIsOrderInfoContainerOpen: PropTypes.func,
  orderItems: PropTypes.array,
  setHighlightedRow: PropTypes.func,
};

const orderAssistanceTypeId = 1;

const isSameItem = (item, row) => {
  if (item.productId > 0) return item.productId === row.productId || item.productId === row.id;
  // punchout items will always have a productId of 0, check for unique combination of vendorItemId, vendor, and uomType instead
  return item.vendorItemId === row.vendorItemId && item.vendor === row.vendor && item.uomType === row.uomType;
};

const getLineText = (element) => element.textContent.replace(/\s+/g, ' ').trim();

// The editor reformats the item html once it's inserted (the <li> becomes a <p>), so find each item's line by its text
const removeItemsFromMessage = (message, items) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(message, 'text/html');
  items.forEach((item) => {
    const itemText = getLineText(parser.parseFromString(item.hyperlink, 'text/html').body);
    const line = Array.from(doc.body.querySelectorAll('p, li')).find((el) => getLineText(el) === itemText);
    if (!line) return;
    // also remove the blank line that was added above the item
    const prev = line.previousElementSibling;
    if (prev && !getLineText(prev) && !prev.querySelector('img')) prev.remove();
    line.remove();
  });
  return doc.body.innerHTML;
};

// getClickedOrderItem reads the PS# and SKU out of this line, so update both together if the format changes
const getItemLineHtml = (row) =>
  `<br>\n\n<li>${
    row.productId > 0
      ? `<a href="/dashboard/admin/catalog/${row.productId}" rel="noopener noreferrer" target="_blank">
            PS# ${row.productId}</a>-`
      : ''
  }${row.description} | Qty: ${row.quantity} | SKU: ${row.vendorItemId} | Price per Item: ${fCurrency(row.price)} | Total ${fCurrency(row.price * row.quantity)}</li>`;

const getClickedOrderItem = (target, orderItems) => {
  const line = !target.closest('a') && target.closest('p, li');
  const lineText = line ? getLineText(line) : '';
  const vendorItemId = lineText.match(/\| SKU: (.*?) \| Price per Item:/)?.[1];
  if (!vendorItemId) return null;
  const productId = Number(lineText.match(/PS# (\d+)/)?.[1] ?? 0);
  if (productId > 0) return orderItems?.find((row) => row.productId === productId);
  return orderItems?.find((row) => !(row.productId > 0) && row.vendorItemId === vendorItemId);
};

const Delta = Quill.import('delta');

const insertAtCursor = (quill, html) => {
  const { index } = quill.getSelection(true) ?? { index: 0 };
  const paste = quill.clipboard.convert(html);
  const pasteLength = paste.length();
  // convert drops the trailing line break, so add it back to keep the text after the cursor on its own line
  if (quill.getText(index, 1) !== '\n') paste.insert('\n');
  quill.updateContents(new Delta().retain(index).concat(paste), 'user');
  quill.setSelection(index + pasteLength, 0, 'silent');
};

// Swap in new html as an edit, so the cursor keeps its spot in the text.
// Setting the message state instead makes ReactQuill put back an old copy of the cursor, which can land at the end.
const replaceKeepingCursor = (quill, html) => {
  // convert drops the trailing line break that editor contents always end with
  const change = quill.getContents().diff(quill.clipboard.convert(html).insert('\n'));
  const { index } = quill.selection.savedRange;
  quill.updateContents(change, 'user');
  // Quill only moves the cursor along with the change while the editor has focus
  if (!quill.hasFocus()) quill.selection.savedRange = { index: change.transformPosition(index), length: 0 };
};

export default function OrderTicket({
  setOpenTicket,
  openTicket,
  selectedRows,
  setAddItemsToTicket,
  addItemsToTicket,
  setSelected,
  currentTicket,
  setCurrentTicket,
  openSidebar,
  setOpenSidebar,
  isOrderInfoContainerOpen,
  setIsOrderInfoContainerOpen,
  orderItems,
  setHighlightedRow,
}) {
  const fileInputRef = useRef(null);
  const { id = '' } = useParams();
  const dispatch = useDispatch();

  const {
    data: { ticketCategories, users: usersList },
  } = useSelector((state) => state.tickets);
  const {
    mainTicket,
    users: usersListById,
    subtickets,
  } = useSelector((state) => state.tickets.data.ticketInfoById[currentTicket]) || {};
  const { user } = useSelector((state) => state.user);
  const { orderById } = useSelector((state) => state.orders || {});
  const { linkedTickets = [], department = '', submittedBy = {} } = orderById[id] || {};

  const scrollRef = useRef(null);
  const editorRef = useRef(null);
  const { addNewTabAndNavigate } = useTabs();

  const signature = useSettingsCheck('AutoSignatureOnTickets');

  const [currentTicketMessage, setCurrentTicketMessage] = useState(signature?.show ? signature?.value : '');
  const [ticketItems, setTicketItems] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [newUsers, setNewUsers] = useState();
  const [selectedTicketCategory, setSelectedTicketCategory] = useState(null);
  const [sendingTicketMessage, setSendingTicketMessage] = useState(false);
  const [displayedTicket, setDisplayedTicket] = useState(currentTicket);
  const [possibleUsers, setPossibleUsers] = useState([]);
  const { users: currentUsers } = useSelector((state) => state.tickets.data.ticketById[displayedTicket]) || [];
  const { allTicketUsers } = useSelector((state) => state.tickets.data.ticketInfoById[displayedTicket] || {}) || {};
  const { ticketMessages } = useSelector(
    (state) => state.tickets.data.ticketById[currentTicket || displayedTicket] || []
  );

  useEffect(() => {
    setSelectedTicketCategory(
      mainTicket?.ticketCategoryId === 0
        ? 0
        : ticketCategories?.find((val) => val.value === mainTicket?.ticketCategoryId)?.value || null
    );
  }, [mainTicket, ticketCategories]);

  useEffect(() => {
    if (selectedTicketCategory === null && ticketCategories) {
      const fountCat = ticketCategories.find((cat) => cat.label === department)?.value;
      if (fountCat) setSelectedTicketCategory(fountCat);
    }
  }, [ticketCategories, selectedTicketCategory, department, openTicket]);

  useEffect(() => {
    if (openTicket === 'open' && !mainTicket?.id && selectedTicketCategory !== null && orderById[id]?.facilityId) {
      getPossibleTicketUsers(orderById[id].facilityId, selectedTicketCategory, orderAssistanceTypeId)
        .then((res) => setPossibleUsers(res.data ?? []))
        .catch(() => setPossibleUsers([]));
    }
  }, [mainTicket, ticketCategories, selectedTicketCategory, orderById, id, openTicket]);

  const users = useMemo(() => usersListById || possibleUsers || [], [usersListById, possibleUsers]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [ticketMessages, allTicketUsers]);

  useEffect(() => setDisplayedTicket(currentTicket), [currentTicket]);

  useEffect(() => {
    if (!openSidebar) setOpenTicket('close');
  }, [openSidebar]);

  useEffect(() => {
    if (openTicket === 'open' && !usersList?.length) dispatch(getFacilitiesAndUsersList());
  }, [openTicket]);

  useEffect(() => {
    if (displayedTicket) {
      dispatch(getTicketData(displayedTicket));
      dispatch(getTicketMessages({ id: displayedTicket }));
    }
  }, [displayedTicket, dispatch]);

  const usersListLookup = useMemo(() => {
    if (!usersList && !users) return {};
    if (!usersList) {
      return users.reduce((acc, user) => {
        acc[user.id.toLowerCase()] = user.value;
        return acc;
      }, {});
    }
    if (!users) {
      return usersList.reduce((acc, user) => {
        acc[user.id.toLowerCase()] = user.value;
        return acc;
      }, {});
    }
    return [...usersList, ...users].reduce((acc, user) => {
      acc[user.id.toLowerCase()] = user.value;
      return acc;
    }, {});
  }, [usersList, users]);

  useEffect(() => {
    const uniqueUsers =
      currentUsers?.reduce((acc, user) => {
        if (!acc.some((u) => u.id === user.userEmail)) {
          acc.push({
            id: user.userEmail,
            value: `${user.userName || usersList?.find((ul) => ul.id.toLowerCase() === user.userEmail)?.value || user.userEmail}`,
          });
        }
        return acc;
      }, []) ??
      (mainTicket
        ? null
        : submittedBy?.email && user.email !== submittedBy.email
          ? [{ id: user.email }, { id: submittedBy.email }]
          : null);

    setNewUsers(
      uniqueUsers?.map((u) => ({
        value: usersListLookup[u.id.toLowerCase()] || u.value || u.id,
        id: u.id,
      }))
    );
  }, [displayedTicket, currentUsers, usersList, mainTicket, openTicket]);

  const handleSetOpenTicket = (newValue) => {
    setOpenTicket(newValue);
    if (newValue === 'close') setCurrentTicket(0);
  };

  const listOfUsers = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const mentionElements = doc.querySelectorAll('span.mention');
    const users = Array.from(mentionElements).map((mention) => mention.getAttribute('data-id'));
    return users;
  };

  const sendMessage = (ticketId, users) => {
    dispatch(
      sendTicketMessage({
        message: currentTicketMessage,
        ticketNumber: ticketId,
        files: attachments,
        notify: true,
        users,
        sentBy: user.email,
        fullName: `${user.firstName} ${user.lastName}`,
      })
    ).finally(() => setSendingTicketMessage(false));
  };

  const getTicketUsers = () => {
    const usersList = listOfUsers(currentTicketMessage);
    const currentUsers = newUsers?.map((t) => t.id) ?? [];
    return [...usersList, ...currentUsers];
  };

  const handleSend = async () => {
    if (!currentTicketMessage && attachments?.length === 0) return;
    const combinedUsers = getTicketUsers();
    setNewUsers(
      combinedUsers?.map((u) => ({
        value: u?.value || usersListLookup[u?.id?.toLowerCase()] || usersListLookup[u?.toLowerCase() || u?.id || u],
        id: u?.id || u,
      }))
    );

    setSendingTicketMessage(true);
    if (!mainTicket?.id) {
      postAddTicket({
        subject: `Ticket order ${id}`,
        message: currentTicketMessage,
        ticketOrderId: id,
        facilityId: orderById[id]?.facilityId,
        files: attachments,
        notify: true,
        users: combinedUsers,
        ticketCategoryId: selectedTicketCategory,
      })
        .then((response) => {
          dispatch(getTicketMessages({ id: response?.data?.id })).then(() => {
            dispatch(addLinkedTicketsToOrder({ orderId: id, idToAdd: response.data.id }));
            setCurrentTicket(response?.data?.id);
          });
        })
        .catch(() => {
          enqueueSnackbar('Failed to send message. Please try again.', { variant: 'error' });
        })
        .finally(() => setSendingTicketMessage(false));
    } else sendMessage(displayedTicket, combinedUsers);

    // Put the cursor back at the top for the next message.
    // blur clears ReactQuill's own copy of the cursor, which it would otherwise put back when the message is cleared.
    const quill = editorRef.current?.getEditor();
    if (quill) {
      editorRef.current.blur();
      quill.selection.savedRange = { index: 0, length: 0 };
    }
    setCurrentTicketMessage(signature?.show ? signature?.value : '');
    setAttachments([]);
    setTicketItems([]);
    setSelected([]);
  };

  const addBasicTicket = () => {
    setSendingTicketMessage(true);
    postAddTicket({
      subject: `Ticket order ${id}`,
      ticketOrderId: id,
      facilityId: orderById[id]?.facilityId,
      users: getTicketUsers(),
      ticketCategoryId: selectedTicketCategory,
    })
      .then((response) => {
        const encodedMessage = encodeURIComponent(currentTicketMessage);
        addNewTabAndNavigate(`/dashboard/tickets/${response.data.id}?draft=${encodedMessage}`);
      })
      .catch(() => {
        enqueueSnackbar('Failed to create ticket. Please try again.', { variant: 'error' });
      })
      .finally(() => setSendingTicketMessage(false));
  };

  useEffect(() => {
    const quill = editorRef.current?.getEditor();
    if (selectedRows && quill) {
      const newItems = selectedRows
        .filter((row) => !ticketItems.some((item) => isSameItem(item, row)))
        .map((row) => ({
          productId: row.productId,
          vendorItemId: row.vendorItemId,
          vendor: row.vendor,
          uomType: row.uomType,
          hyperlink: getItemLineHtml(row),
        }));

      const removedItems = ticketItems.filter((item) => !selectedRows.some((row) => isSameItem(item, row)));

      // Add new items to ticketItems state and the editor at the cursor, and take out any items that were unchecked
      if (newItems.length || removedItems.length) {
        setTicketItems((prevItems) => [...prevItems.filter((item) => !removedItems.includes(item)), ...newItems]);
        if (removedItems.length)
          replaceKeepingCursor(quill, removeItemsFromMessage(quill.root.innerHTML, removedItems));
        if (newItems.length) insertAtCursor(quill, newItems.map((item) => item.hyperlink).join(''));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows]);

  const handleChangeMessage = (value) => ticketMessageInput(value, setCurrentTicketMessage);

  const handleFileInputChange = (event) => handleAddTicketAttachment(event, attachments, setAttachments);
  const removeFile = (file) => () => setAttachments((prevAttachments) => prevAttachments.filter((x) => x !== file));

  const handleMessageListClick = (event) => {
    const orderItem = getClickedOrderItem(event.target, orderItems);
    if (orderItem) setHighlightedRow(orderItem.id);
  };

  // Set the cursor on the element directly instead of through state, so hovering doesn't re-render the ticket
  const handleMessageListMouseOver = (event) => {
    event.currentTarget.style.cursor = getClickedOrderItem(event.target, orderItems) ? 'pointer' : '';
  };

  const handleCloseTicket = () => {
    handleSetOpenTicket('close');
    setAddItemsToTicket(false);
    setCurrentTicketMessage(signature?.show ? signature?.value : '');
    setTicketItems([]);
    setSelected([]);
  };

  return (
    <Box sx={{ borderRadius: '5px', height: '100%', display: 'flex', alignItems: 'flex-end' }}>
      {openSidebar && openTicket !== 'close' && (
        <Card sx={{ p: 1, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
          <TicketHeader
            handleSetOpenTicket={handleSetOpenTicket}
            currentTicket={currentTicket}
            currentTicketMessage={currentTicketMessage}
            handleCloseTicket={handleCloseTicket}
            subtickets={subtickets}
            mainTicket={mainTicket}
            setDisplayedTicket={setDisplayedTicket}
            displayedTicket={displayedTicket}
            newUsers={newUsers}
            setNewUsers={setNewUsers}
            isOrderInfoContainerOpen={isOrderInfoContainerOpen}
            setIsOrderInfoContainerOpen={setIsOrderInfoContainerOpen}
            addBasicTicket={addBasicTicket}
            submittingTicket={sendingTicketMessage}
          />
          <TicketCategory
            ticketId={displayedTicket}
            style={{ marginLeft: '20px' }}
            selectedTicketCategory={selectedTicketCategory ?? 0}
            setSelectedTicketCategory={setSelectedTicketCategory}
            addOtherCategory
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '70vh', minHeight: 0, flexGrow: 1 }}>
            <Box
              ref={scrollRef}
              onClick={handleMessageListClick}
              onMouseOver={handleMessageListMouseOver}
              sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', my: 1 }}
            >
              {displayedTicket !== 0 && <TicketMessageList ticketId={displayedTicket} />}
            </Box>
            <Editor
              attachFile
              verySimple
              printChat
              id="ticket-reply"
              value={currentTicketMessage}
              aiHelp
              onChange={handleChangeMessage}
              placeholder="Type a message"
              sx={{ borderColor: 'transparent', flexShrink: 0 }}
              users={users || []}
              sendIcon
              disableSendTooltip={sendingTicketMessage ? 'Sending message...' : 'Choose a category to send'}
              disableSend={mainTicket?.ticketCategoryId === 0 && ticketCategories?.length > 0}
              sendingTicketMessage={sendingTicketMessage}
              handleSend={handleSend}
              handleFileInputChange={handleFileInputChange}
              addItemsToTicket={addItemsToTicket}
              setAddItemsToTicket={setAddItemsToTicket}
              displayedTicket={displayedTicket}
              currentTicket={currentTicket}
              attachments={attachments}
              removeFile={removeFile}
              editorRef={editorRef}
            />
          </Box>
          <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileInputChange} />
        </Card>
      )}
      {(openTicket === 'close' || !openSidebar) && (
        <TicketButtons
          setCurrentTicket={setCurrentTicket}
          openTicket={openTicket}
          setCurrentTicketMessage={setCurrentTicketMessage}
          signature={signature}
          setAttachments={setAttachments}
          currentTicket={currentTicket}
          handleSetOpenTicket={handleSetOpenTicket}
          setAddItemsToTicket={setAddItemsToTicket}
          setOpenSidebar={setOpenSidebar}
          direction={openSidebar ? 'horizontal' : 'vertical'}
          linkedTickets={linkedTickets}
        />
      )}
    </Box>
  );
}
